import assertNever from '@watershed/shared-util/src/assertNever';

import { ASTExpr, ASTProgram, stringifyExpr } from './ast';
import { Context } from './types';
import { assertComparable, assertNumber, debugAssert } from './assert';
import { Quantity } from './quantity';
import { typecheckExpression } from './typecheck';
import { getFunction } from './functions';
import { convertToType } from './typeConversion';

export function evalExpression(ctx: Context, expr: ASTExpr): unknown {
  switch (expr.t) {
    case 'string':
    case 'number':
    case 'boolean':
      return expr.v;
    case 'unary':
      switch (expr.op) {
        case '-': {
          const v = evalExpression(ctx, expr.expr);
          if (typeof v === 'number') {
            return -v;
          } else if (v instanceof Quantity) {
            return new Quantity(-v, v.unit);
          } else {
            throw new Error(
              `error: unknown type ${v} for unary minus. Expected Quantity or number`
            );
          }
        }
        default:
          assertNever(expr);
      }
    case 'identifier': {
      const v = ctx.variables.get(expr.name);
      debugAssert(
        v !== undefined,
        () =>
          `unknown variable ${expr.name} (know about ${[
            ...ctx.variables.keys(),
          ].join(', ')})`
      );
      return v;
    }
    case 'binary': {
      const l = assertNumber(evalExpression(ctx, expr.l));
      const r = assertNumber(evalExpression(ctx, expr.r));
      switch (expr.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          if (r === 0) {
            throw new Error(
              `error: cannot divide by zero in expression ${stringifyExpr(
                expr
              )}`
            );
          }
          return l / r;
        default:
          assertNever(expr);
      }
    }
    case 'comparison': {
      const l = assertComparable(evalExpression(ctx, expr.l));
      const r = assertComparable(evalExpression(ctx, expr.r));
      switch (expr.op) {
        case '==':
          return l === r;
        case '!=':
          return l !== r;
        case '<':
          if (l === null || r === null) {
            return null;
          }
          return l < r;
        case '<=':
          if (l === null || r === null) {
            return null;
          }
          return l <= r;
        case '>':
          if (l === null || r === null) {
            return null;
          }
          return l > r;
        case '>=':
          if (l === null || r === null) {
            return null;
          }
          return l >= r;
        default:
          assertNever(expr);
      }
    }
    case 'call': {
      const name = expr.name;
      const fn = getFunction(name);
      debugAssert(
        fn != null,
        () => `unknown function ${name} in expression ${stringifyExpr(expr)}`
      );
      return fn.eval(ctx, expr);
    }
    case 'null':
      return null;
    default:
      assertNever(expr);
  }
}

export function evalProgram(
  ctx: Context,
  prog: ASTProgram
): Record<string, unknown> {
  ctx = {
    ...ctx,
    variables: new Map(ctx.variables),
  };
  const out: Record<string, unknown> = {};
  for (const line of prog) {
    let v: unknown;
    if (line.typeDeclaration) {
      v = convertToType(ctx, {
        fromExpr: line.expr,
        toType: line.typeDeclaration,
      }).eval();
    } else {
      v = evalExpression(ctx, line.expr);
    }

    ctx.variables.set(line.name, v);
    ctx.variablesType.set(
      line.name,
      line.typeDeclaration ?? typecheckExpression(ctx, line.expr)
    );
    out[line.name] = v;
  }
  return out;
}
